<script setup>
import { computed } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';

import { Edit16, TrashCan16, View16 } from '@carbon/icons-vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  canManagePublicMacros: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['edit', 'delete']);
const { t } = useI18n();

const createdByName = computed(() => {
  const createdBy = props.macro.created_by;
  return createdBy.available_name ?? createdBy.email ?? '';
});

const updatedByName = computed(() => {
  const updatedBy = props.macro.updated_by;
  return updatedBy.available_name ?? updatedBy.email ?? '';
});

const visibilityLabel = computed(() => {
  const i18nKey =
    props.macro.visibility === 'global'
      ? 'MACROS.EDITOR.VISIBILITY.GLOBAL.LABEL'
      : 'MACROS.EDITOR.VISIBILITY.PERSONAL.LABEL';
  return t(i18nKey);
});

const canManageMacro = computed(
  () => props.canManagePublicMacros || props.macro.visibility !== 'global'
);

// R2-9-4: the kit's ⋮ menu. A public macro someone may not manage opens read-only ("View") and has no Delete.
const menuItems = computed(() => [
  canManageMacro.value
    ? { label: t('SETTINGS_TABLE.EDIT'), value: 'edit', icon: Edit16 }
    : { label: t('MACROS.VIEW.TOOLTIP'), value: 'edit', icon: View16 },
  ...(canManageMacro.value
    ? [
        {
          label: t('SETTINGS_TABLE.DELETE'),
          value: 'delete',
          icon: TrashCan16,
          destructive: true,
        },
      ]
    : []),
]);

const onAction = action => {
  if (action === 'delete') emit('delete');
  else emit('edit');
};
</script>

<template>
  <BaseTableRow :item="macro">
    <BaseTableCell wrap>{{ macro.name }}</BaseTableCell>
    <BaseTableCell>
      <div v-if="macro.created_by" class="flex min-w-0 items-center gap-2">
        <Avatar
          :name="createdByName"
          :size="24"
          rounded-full
          class="shrink-0"
        />
        <span class="truncate">{{ createdByName }}</span>
      </div>
      <template v-else>--</template>
    </BaseTableCell>
    <BaseTableCell>
      <div v-if="macro.updated_by" class="flex min-w-0 items-center gap-2">
        <Avatar
          :name="updatedByName"
          :size="24"
          rounded-full
          class="shrink-0"
        />
        <span class="truncate">{{ updatedByName }}</span>
      </div>
      <template v-else>--</template>
    </BaseTableCell>
    <BaseTableCell>{{ visibilityLabel }}</BaseTableCell>
    <BaseTableCell actions>
      <RowMenu
        :label="t('SETTINGS_TABLE.ROW_ACTIONS', { name: macro.name })"
        :items="menuItems"
        @action="onAction"
      />
    </BaseTableCell>
  </BaseTableRow>
</template>
